import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import type { IndicatorRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  GetIndicatorsQuery,
  Indicator_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetIndicatorsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

const useIndicatorGraphqlVariables = () => {
  const whereFilter = useEntityWhereFilter<Indicator_Bool_Exp>(
    Parent_Type_Enum.Indicator
  );

  return { where: whereFilter };
};

/**
 * Maps TRPC indicator data to match the GraphQL query structure
 * Exported for use by data sources
 */
export function mapTrpcIndicatorsToGraphQL(
  trpcData: IndicatorRegisterResponse | undefined
): StrictMappedGraphQLSelection<GetIndicatorsQuery> | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    indicator: trpcData.indicators,
  };
}

type InternalArgs = {
  entityIds?: string[];
};

const useGetIndicatorRegisterBase = createQueryHook<
  InternalArgs,
  IndicatorRegisterResponse,
  GetIndicatorsQuery
>({
  trpcQueryOptions: (trpc, args) =>
    trpc.frontend.indicator.register.queryOptions({
      entityIds: args.entityIds,
    }),
  mapTrpcDataToGraphQL: (data) => ({ indicator: data.indicators }),
  graphqlDocument: GetIndicatorsDocument,
  graphqlVariables: useIndicatorGraphqlVariables,
});

/**
 * Fetches indicators with automatic entity filtering for both GraphQL and tRPC
 * paths. Entity filtering uses the `useEntityFilter` context internally —
 * consumers don't need to pass entityIds.
 */
export const useGetIndicatorRegister = (params: {
  queryArgs: Record<string, never>;
  shouldSkip?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetIndicatorRegisterBase({
    queryArgs: {
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
  });
};
